import { inject, provide, ref, computed } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useRouter } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
// Controle de Acesso (Kairo): a tela de Controle de Acesso e a AUTORIDADE de
// visibilidade do menu. Se um item tem feature key no catalogo e NAO esta
// bloqueado (verde) para o usuario, ele aparece INDEPENDENTE da permissao
// NATIVA de papel do Chatwoot (`meta.permissions`) — exatamente a mesma regra
// que o guard de rota (routeHelpers.js#validateActiveAccountRoutes) ja aplica.
// Assim o menu deixa de divergir do guard: "marcou verde => aparece p/ qualquer
// papel". Seguranca de mutacao continua no servidor (Pundit/policies).
import {
  getBlockedFeatureKeys,
  featureKeyForRouteName,
} from 'dashboard/constants/accessControl.js';

const SidebarControl = Symbol('SidebarControl');

const DEFAULT_WIDTH = 200;
const MIN_WIDTH = 56;
const COLLAPSED_THRESHOLD = 160;
const MAX_WIDTH = 320;

// Shared state for active popover (only one can be open at a time)
const activePopover = ref(null);
let globalCloseTimeout = null;

export function useSidebarResize() {
  const { uiSettings, updateUISettings } = useUISettings();

  const sidebarWidth = ref(uiSettings.value.sidebar_width || DEFAULT_WIDTH);
  const isCollapsed = computed(() => sidebarWidth.value < COLLAPSED_THRESHOLD);

  const setSidebarWidth = width => {
    sidebarWidth.value = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
  };

  const saveWidth = () => {
    updateUISettings({ sidebar_width: sidebarWidth.value });
  };

  const snapToCollapsed = () => {
    sidebarWidth.value = MIN_WIDTH;
    updateUISettings({ sidebar_width: MIN_WIDTH });
  };

  const snapToExpanded = () => {
    sidebarWidth.value = DEFAULT_WIDTH;
    updateUISettings({ sidebar_width: DEFAULT_WIDTH });
  };

  return {
    sidebarWidth,
    isCollapsed,
    setSidebarWidth,
    saveWidth,
    snapToCollapsed,
    snapToExpanded,
    MIN_WIDTH,
    MAX_WIDTH,
    COLLAPSED_THRESHOLD,
    DEFAULT_WIDTH,
  };
}

export function usePopoverState() {
  const setActivePopover = name => {
    clearTimeout(globalCloseTimeout);
    activePopover.value = name;
  };

  const closeActivePopover = () => {
    activePopover.value = null;
  };

  const scheduleClose = (delay = 150) => {
    clearTimeout(globalCloseTimeout);
    globalCloseTimeout = setTimeout(() => {
      closeActivePopover();
    }, delay);
  };

  const cancelClose = () => {
    clearTimeout(globalCloseTimeout);
  };

  return {
    activePopover,
    setActivePopover,
    closeActivePopover,
    scheduleClose,
    cancelClose,
  };
}

export function useSidebarContext() {
  const context = inject(SidebarControl, null);
  if (context === null) {
    throw new Error(`Component is missing a parent <Sidebar /> component.`);
  }

  const router = useRouter();
  const { shouldShow } = usePolicy();
  const currentUser = useMapGetter('getCurrentUser');

  const resolvePath = to => {
    if (to) return router.resolve(to)?.path || '/';
    return '/';
  };

  // Helper to find route definition by name without resolving
  const findRouteByName = name => {
    const routes = router.getRoutes();
    return routes.find(route => route.name === name);
  };

  // Nome da rota alvo (mesma regra de navigationPath do resolvePermissions).
  const resolveRouteName = to => {
    if (!to) return null;
    if (to.params?.navigationPath) {
      return findRouteByName(to.params.navigationPath)?.name || null;
    }
    return router.resolve(to)?.name || null;
  };

  // Controle de Acesso liberou (verde) esta rota para este usuario?
  const liberadoPeloControleDeAcesso = to => {
    const featureKey = featureKeyForRouteName(resolveRouteName(to));
    if (!featureKey) return false; // sem key no catalogo => regra nativa manda
    return !getBlockedFeatureKeys(currentUser.value).includes(featureKey);
  };

  // PONTO UNICO do bypass: TODO consumidor de permissao do menu passa por aqui
  // — `<Policy :permissions="resolvePermissions(to)">` do SidebarGroupLeaf e do
  // SidebarGroup (o gate que REALMENTE esconde cada item) e o `isAllowed` abaixo.
  // Se o item esta VERDE no Controle de Acesso, devolvemos [] (sem exigencia de
  // papel), e `usePolicy#checkPermissions([])` => true. Assim "marcou verde =>
  // aparece p/ qualquer papel", igual ao guard de rota (routeHelpers.js).
  // featureFlag/installationType continuam valendo (nao revelamos feature que a
  // conta nao tem). Item BLOQUEADO mantem a permissao nativa (e o
  // stripBlockedMenuItems ja o removeu do menu antes).
  const resolvePermissions = to => {
    if (!to) return [];

    if (liberadoPeloControleDeAcesso(to)) return [];

    // If navigationPath param exists, get the target route definition
    if (to.params?.navigationPath) {
      const targetRoute = findRouteByName(to.params.navigationPath);
      return targetRoute?.meta?.permissions ?? [];
    }

    return router.resolve(to)?.meta?.permissions ?? [];
  };

  const resolveFeatureFlag = to => {
    if (!to) return '';

    // If navigationPath param exists, get the target route definition
    if (to.params?.navigationPath) {
      const targetRoute = findRouteByName(to.params.navigationPath);
      return targetRoute?.meta?.featureFlag || '';
    }

    return router.resolve(to)?.meta?.featureFlag || '';
  };

  const resolveInstallationType = to => {
    if (!to) return [];

    // If navigationPath param exists, get the target route definition
    if (to.params?.navigationPath) {
      const targetRoute = findRouteByName(to.params.navigationPath);
      return targetRoute?.meta?.installationTypes || [];
    }

    return router.resolve(to)?.meta?.installationTypes || [];
  };

  const isAllowed = to => {
    // resolvePermissions ja aplica o bypass do Controle de Acesso (ponto unico).
    const permissions = resolvePermissions(to);
    const featureFlag = resolveFeatureFlag(to);
    const installationType = resolveInstallationType(to);

    return shouldShow(featureFlag, permissions, installationType);
  };

  return {
    ...context,
    resolvePath,
    resolvePermissions,
    resolveFeatureFlag,
    isAllowed,
  };
}

export function provideSidebarContext(context) {
  provide(SidebarControl, context);
}
